import { jwtDecode } from "jwt-decode"
import { E2E_TOKEN_ISSUER } from "../common/e2e"

/**
 * Refresh an ID token this long before it actually expires. Without a buffer a
 * token with two seconds left is cached, returned, sent, and then rejected
 * server-side. Well inside the Firebase SDK's own ~5-minutes-early refresh
 * window, so in practice this costs no extra network calls.
 */
export const TOKEN_EXPIRY_SKEW_MS = 120_000

/**
 * Google securetoken error messages that mean the refresh token itself is
 * dead. Anything else — a network blip, a timeout, a 5xx — must NOT cause
 * stored tokens to be cleared, or a single offline moment permanently destroys
 * the session with no way back but a re-login.
 */
const UNRECOVERABLE_REFRESH_ERRORS = [
  "TOKEN_EXPIRED",
  "INVALID_REFRESH_TOKEN",
  "USER_DISABLED",
  "USER_NOT_FOUND",
  "MISSING_REFRESH_TOKEN",
]

/**
 * Firebase Auth SDK error codes that mean the signed-in user can never mint
 * another token. Anything else — a network blip, a storage write the browser
 * refused — must NOT end the session.
 */
const UNRECOVERABLE_SDK_TOKEN_ERROR_CODES = [
  "auth/user-token-expired",
  "auth/user-disabled",
  "auth/invalid-user-token",
  "auth/user-not-found",
]

export const isUnrecoverableSdkTokenErrorCode = (code: string): boolean =>
  UNRECOVERABLE_SDK_TOKEN_ERROR_CODES.includes(code)

export const isIdTokenFresh = (idToken: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(idToken)
    if (!exp) return false
    return exp * 1000 - TOKEN_EXPIRY_SKEW_MS > Date.now()
  } catch {
    return false
  }
}

/**
 * E2E tokens are unsigned JWTs with no Firebase user behind them, seeded
 * alongside a fake refresh token. They must never reach the Firebase or
 * securetoken refresh paths.
 */
export const isE2eIdToken = (idToken: string): boolean => {
  try {
    return jwtDecode<{ iss?: string }>(idToken).iss === E2E_TOKEN_ISSUER
  } catch {
    return false
  }
}

/** Structural rather than axios-typed, so this stays dependency-free. */
export const findRefreshErrorMessage = (error: unknown): string | null => {
  if (typeof error !== "object" || error === null) return null
  if (!("response" in error)) return null

  const { response } = error
  if (typeof response !== "object" || response === null) return null
  if (!("data" in response)) return null

  const { data } = response
  if (typeof data !== "object" || data === null) return null
  if (!("error" in data)) return null

  const inner = data.error
  if (typeof inner !== "object" || inner === null) return null
  if (!("message" in inner)) return null

  return typeof inner.message === "string" ? inner.message : null
}

export const isUnrecoverableRefreshError = (error: unknown): boolean => {
  const message = findRefreshErrorMessage(error)
  if (!message) return false

  return UNRECOVERABLE_REFRESH_ERRORS.some((code) => message.startsWith(code))
}
